import { useCallback, useMemo, useState } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import { Box } from '@mui/material';
import { CardWrapper, FormFooter } from '@styles/ContactManager/DownPaymentModalStyles';
import { PageWrapper } from '@shared/styledComponents';
import { PhoenixGridHeader } from '@modules/admin/Tools/LenderUtility/shared/PhoenixGridHeader';
import { FormPart } from '@modules/shared/components/FormPart';
import { CreditCardInputField } from '@shared/fields/CreditCardInputField';
import { CvvInputField } from '@shared/fields/CvvInputField';
import { NumberInputField } from '@shared/fields/NumberInputField';
import { SelectDateField } from '@shared/fields/SelectDateField';
import { TextInputField } from '@shared/fields/TextInputField';
import { SaveButton, CancelButton } from '@shared/inputs/Buttons';
import { SelectAutocompleteField } from '@shared/fields/SelectAutocompleteField';
import { Roles } from '@shared/constants/constants';
import { useUserAccount } from '@shared/hooks/useUserAccount';
import {
  CREDIT_CARD_PAYMENT_PROCESS_STATUSES_OPTIONS,
  CREDIT_CARD_TYPE_OPTIONS,
  CreditCardPaymentProcessStatusEnum,
  ICardFormData,
  PAYMENT_TYPE_OPTIONS,
  PaymentTypeEnum,
  Card
} from '../../types';
import { savePaymentValidation } from '../../validation';

enum SubmitType {
  SavePayment,
  ProcessPayment
}

interface ICardFormProps {
  handleSubmitSaveCard: (data: ICardFormData, formikHelpers: FormikHelpers<ICardFormData>) => void;
  handleProcessPayment: (data: ICardFormData, formikHelpers: FormikHelpers<ICardFormData>) => void;
  handleCopyCard?: () => void;
  handleUpdateCard?: () => void;
  handleCancelPayment?: () => void;
  data?: Card | null;
  paymentIndex?: number;
  isSaved?: boolean;
  initialDownPayment: number;
}

export const CardForm = ({
  data,
  handleSubmitSaveCard,
  handleProcessPayment,
  handleCancelPayment,
  handleUpdateCard,
  handleCopyCard,
  paymentIndex,
  initialDownPayment,
  isSaved = false
}: ICardFormProps) => {
  const { isAdmin, userHasRoles } = useUserAccount();

  const [submitType, setSubmitType] = useState<SubmitType>(SubmitType.SavePayment);

  const disableSaveProcessingBtn = !(isAdmin || userHasRoles([Roles.CREDIT_CARD_PROCESSING]));

  const handlerSubmit = useCallback((type: SubmitType, handleSubmit: () => void) => {
    setSubmitType(type);
    handleSubmit();
  }, []);

  const isDisabled = useMemo(() => isSaved || data?.isCopied, [isSaved, data?.isCopied]);
      
  const isPendingStatus = useMemo(
    () => {
      if (data?.processingStatus) {
        return data?.processingStatus === CreditCardPaymentProcessStatusEnum.PENDING;
      }

      return true;
    },
    [data?.processingStatus]
  );

  const initialFormValues: ICardFormData = useMemo(
    () => ({
      holderName: data?.holderName || '',
      cardNumber: data?.lastFourDigits ? `**** **** **** ${data?.lastFourDigits}` : '',
      processingDate: data?.processingDate || null,
      processedDate: data?.processedDate || new Date(),
      processingStatus: data?.processingStatus || CreditCardPaymentProcessStatusEnum.PENDING,
      cvv: data?.cvv ? (isDisabled ? '***' : data?.cvv) : '',
      expiryDate: data?.expiryDate || null,
      amount: data?.amount || initialDownPayment || 0,
      creditCardType: data?.creditCardType || null,
      paymentType: data?.paymentType || PaymentTypeEnum.DownPayment.replace(' ', '') as keyof typeof PaymentTypeEnum,
      notes: data?.notes || '',
      downPaymentId: data?.downPaymentId
    }),
    [data, initialDownPayment]
  );

  const validationSchema = useMemo(
    () => savePaymentValidation(isSaved),
    [isSaved]
  );

  const formConfig = useMemo(
    () => [
      {
        width: '50%',
        name: 'creditCardType',
        disabled: isDisabled,
        options: CREDIT_CARD_TYPE_OPTIONS,
        component: SelectAutocompleteField,
      },
      {
        width: '50%',
        name: 'amount',
        prefix: '$',
        decimalScale: 2,
        removeThousandSeparator: true,
        component: NumberInputField,
        disabled: !isPendingStatus,
      },
      {
        width: '50%',
        name: 'cardNumber',
        component: isDisabled ? TextInputField : CreditCardInputField,
        disabled: isDisabled,
      },
      {
        width: '50%',
        name: 'processedDate',
        component: SelectDateField,
        disabled: true,
      },
      {
        width: '50%',
        name: 'cvv',
        label: 'Security code',
        removeThousandSeparator: true,
        component: isDisabled ? TextInputField : CvvInputField,
        disabled: isDisabled,
      },
      {
        width: '50%',
        name: 'processingStatus',
        disabled: true,
        label: 'Status',
        options: CREDIT_CARD_PAYMENT_PROCESS_STATUSES_OPTIONS,
        readOnly: isDisabled,
      },
      {
        width: '50%',
        name: 'expiryDate',
        views: ['year', 'month'],
        format: 'MM/YYYY',
        disabled: isDisabled,
        minDate: new Date(),
        component: SelectDateField,
      },
      {
        width: '50%',
        name: 'processingDate',
        disabled: !isPendingStatus,
        disablePast: true,
        component: SelectDateField,
      },
      {
        width: '50%',
        name: 'holderName',
        component: TextInputField,
        isNotNumeric: true,
        disabled: isDisabled,
      },
      {
        width: '50%',
        name: 'paymentType',
        options: PAYMENT_TYPE_OPTIONS,
        component: SelectAutocompleteField,
        disabled: !isPendingStatus,
      },
      {
        width: '100%',
        height: '300px',
        name: 'notes',
        component: TextInputField,
        disabled: !isPendingStatus,
      },
    ],
    [isPendingStatus, isDisabled]
  );

  return (
    <Formik
      enableReinitialize
      initialValues={initialFormValues}
      validationSchema={validationSchema}
      onSubmit={submitType === SubmitType.SavePayment ? handleSubmitSaveCard : handleProcessPayment}
    >
      {(props) => (
        <Form>
          <CardWrapper>
            <PageWrapper>
              <PhoenixGridHeader title={paymentIndex ? `Payment #${paymentIndex}` : 'Add new payment'}>
                {isSaved && (
                  <SaveButton
                    type="button"
                    label="Copy"
                    onClick={handleCopyCard}
                  />
                )}
              </PhoenixGridHeader>

              <FormPart config={formConfig} />

              <FormFooter disabled={isSaved}>
                {isPendingStatus && isSaved && (
                  <SaveButton
                    color="success"
                    type="button"
                    label="Update Card"
                    onClick={handleUpdateCard}
                  />
                )}

                <Box display="flex">
                  {!isSaved && (
                    <SaveButton
                      color="success"
                      type="button"
                      width="120px"
                      label="Save Payment"
                      isLoading={props.isSubmitting}
                      onClick={() => handlerSubmit(SubmitType.SavePayment, props.handleSubmit)}
                    />
                  )}

                  {isPendingStatus && (
                    <SaveButton
                      type="button"
                      label="Process Payment Now"
                      width="160px"
                      disabled={disableSaveProcessingBtn}
                      isLoading={submitType === SubmitType.ProcessPayment && props.isSubmitting}
                      onClick={() => handlerSubmit(SubmitType.ProcessPayment, props.handleSubmit)}
                    />
                  )}

                  {paymentIndex && isPendingStatus && (
                    <CancelButton
                      width="175px"
                      label="Cancel Payment"
                      onClick={handleCancelPayment}
                    />
                  )}
                </Box>
              </FormFooter>
            </PageWrapper>
          </CardWrapper>
        </Form>
      )}
    </Formik>
  );
};
